import React, { useState } from 'react';
import { Box, Typography, Button, Grow } from '@mui/material';
import { useNavigate } from 'react-router-dom';

interface QuizResultsProps {
  score: number;
  totalQuestions: number;
  strongAreas: string[];
  areasForImprovement: string[];
  title?: string;
}

const QuizResults: React.FC<QuizResultsProps> = ({ 
  score, 
  totalQuestions, 
  strongAreas,
  areasForImprovement,
  title = 'Quiz'
}) => {
  const navigate = useNavigate();
  const [showDetailedResults, setShowDetailedResults] = useState(false);
  
  const renderScoreView = () => (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        maxWidth: '600px',
        width: '100%',
        mb: 4,
      }}
    >
      <Box
        sx={{
          width: 200,
          height: 200,
          borderRadius: '50%',
          background: 'conic-gradient(#6B8BF5 0deg, #6B8BF5 252deg, rgba(255,255,255,0.3) 252deg, rgba(255,255,255,0.3) 360deg)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          mb: 4,
          position: 'relative',
        }}
      >
        <Box
          sx={{
            width: 160,
            height: 160,
            borderRadius: '50%',
            bgcolor: 'white',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Typography variant="caption" sx={{ color: '#999', mb: 1 }}>
            Score
          </Typography>
          <Typography variant="h3" sx={{ fontWeight: 'bold', color: '#333' }}>
            {score}/{totalQuestions}
          </Typography>
        </Box>
      </Box>

      <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'black', mb: 2 }}>
        CONGRATS!
      </Typography>
      <Typography variant="body1" className='font-semibold' sx={{ color: 'rgba(0, 0, 0, 0.6)' }}>
        Now you can check your results...
      </Typography>
    </Box>
  );

  const renderDetailedResults = () => (
    <Box
    className='bg-white rounded-2xl p-4 mb-4'
      sx={{
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        maxWidth: '600px',
        gap: 4,
        p: 3,
      }}
    >
      <Box>
        <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          😎 Strong Areas:
        </Typography>
        <Box sx={{ bgcolor: 'white', borderRadius: 4, p: 3 }}>
          {strongAreas.map((area, index) => (
            <Typography key={index} sx={{ mb: index < strongAreas.length - 1 ? 2 : 0 }}>
              {area}
            </Typography>
          ))}
        </Box>
      </Box>

      <Box>
        <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          💪 Areas for Improvement:
        </Typography>
        <Box sx={{ bgcolor: 'white', borderRadius: 4, p: 3 }}>
          {areasForImprovement.map((area, index) => (
            <Typography key={index} sx={{ mb: index < areasForImprovement.length - 1 ? 2 : 0 }}>
              {area}
            </Typography>
          ))}
        </Box>
      </Box>
    </Box>
  );

  return (
    <div className="flex flex-col gap-6 min-h-screen w-full">
      <Grow in={true} timeout={800}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            minHeight: '100vh',
            width: '100%',
            position: 'relative',
            overflow: 'hidden',
            backgroundImage: 'url("/assets/results-background.png")',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
          }}
        >
          <div className="flex items-center justify-between p-4">
            <button 
              onClick={() => {
                if (showDetailedResults) {
                  setShowDetailedResults(false);
                } else {
                  navigate(-1);
                }
              }} 
              className="w-10 h-10 flex items-center justify-center"
            >
              <svg width="20" height="20" viewBox="0 0 20 20" fill="black" xmlns="http://www.w3.org/2000/svg">
                <path fillRule="evenodd" clipRule="evenodd" d="M15 10C15 10.4142 14.6642 10.75 14.25 10.75L7.61208 10.75L9.76983 12.7094C10.0684 12.9965 10.0777 13.4713 9.79062 13.7698C9.50353 14.0684 9.02875 14.0777 8.73017 13.7906L5.23017 10.5406C5.08311 10.3992 5 10.204 5 10C5 9.79599 5.08311 9.60078 5.23017 9.45938L8.73017 6.20938C9.02875 5.92228 9.50353 5.93159 9.79062 6.23017C10.0777 6.52875 10.0684 7.00353 9.76983 7.29063L7.61208 9.25L14.25 9.25C14.6642 9.25 15 9.58579 15 10Z" fill="black"/>
              </svg>
            </button>
            <div className="bg-[#6B8BF5] text-white px-6 py-2 rounded-full font-medium text-base">
              {title} - Quiz
            </div>
          </div>

          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              flex: 1,
              p: 3,
            }}
          >
            {!showDetailedResults ? (
              <>
                {renderScoreView()}
                <Button
                  fullWidth
                  variant="contained"
                  sx={{
                    bgcolor: '#FF6B6B',
                    borderRadius: '120px',
                    py: "9px",
                    fontSize: '16px',
                    fontWeight: 600,
                    textTransform: 'none',
                    '&:hover': {
                      bgcolor: '#FF5252',
                    },
                  }}
                  onClick={() => setShowDetailedResults(true)}
                >
                  Show results
                </Button>
              </>
            ) : (
              <>
                {renderDetailedResults()}
                <Button
                  fullWidth
                  variant="contained"
                  sx={{
                    bgcolor: '#FF6B6B',
                    borderRadius: '120px',
                    py: "9px",
                    fontSize: '16px',
                    fontWeight: 600,
                    textTransform: 'none',
                    '&:hover': {
                      bgcolor: '#FF5252',
                    },
                  }}
                  onClick={() => navigate('/dashboard')}
                >
                  Go back to dashboard
                </Button>
              </>
            )}
          </Box>
        </Box>
      </Grow>
    </div>
  );
};

export default QuizResults; 